<template>
  <div
    class="flex flex-col bg-[#0F172B] text-white"
    :class="useEmbeddedScroll ? 'h-full min-h-0' : ''"
  >
    <!-- 页面标题 -->
    <div class="shrink-0 px-6 pt-4 pb-4">
      <!-- 标题行：左侧标题 + 右侧搜索框 -->
      <div class="flex items-center justify-between gap-3 mb-5">
        <h1 class="text-2xl font-bold text-white shrink-0">AI Effects</h1>

        <!-- 搜索框 -->
        <div class="relative w-56 shrink-0 mr-8">
          <UIcon
            name="i-lucide-search"
            class="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-white/40 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search effects..."
            class="w-full bg-white/8 border border-white/15 hover:border-white/30 focus:border-[#8A53FC] rounded-full text-sm text-white placeholder-white/35 pl-8 pr-8 py-1.5 outline-none transition-colors"
          />
          <button
            v-if="searchQuery"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
            @click="searchQuery = ''"
          >
            <UIcon name="i-lucide-x" class="size-3.5" />
          </button>
        </div>
      </div>

      <!-- Video / Image 切换 -->
      <div class="flex items-center gap-2 mb-4">
        <button
          v-for="tab in effectTypeTabs"
          :key="tab.value"
          class="px-5 py-1.5 rounded-full text-sm font-semibold transition-colors"
          :class="
            activeEffectType === tab.value
              ? 'bg-[#8A53FC] text-white'
              : 'bg-transparent text-white border border-white/20 hover:border-white/40'
          "
          @click="selectEffectType(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- 分类导航（自适应宽度，自动换行） -->
      <div class="flex flex-wrap items-center gap-x-1 gap-y-2 pb-1">
        <button
          class="px-3 py-1 text-sm font-medium transition-colors relative"
          :class="
            activeCategory === 'all'
              ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#8A53FC] after:rounded-full'
              : 'text-white/50 hover:text-white/80'
          "
          @click="activeCategory = 'all'"
        >
          All
        </button>

        <button
          v-for="cat in categories"
          :key="cat.value"
          class="px-3 py-1 text-sm font-medium transition-colors relative"
          :class="
            activeCategory === cat.value
              ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#8A53FC] after:rounded-full'
              : 'text-white/50 hover:text-white/80'
          "
          @click="activeCategory = cat.value"
        >
          {{ cat.label }}
        </button>
      </div>
    </div>

    <!-- 网格列表：列表页随页面滚动，弹窗内独立滚动 -->
    <div
      ref="gridContainerRef"
      class="px-6 pb-6"
      :class="
        useEmbeddedScroll
          ? 'flex-1 min-h-0 overflow-y-auto custom-scroll effect-grid-scroll'
          : ''
      "
    >
      <!-- 数据请求中：骨架屏（瀑布流列） -->
      <div v-if="pending" class="flex gap-3 items-start">
        <div
          v-for="col in Math.max(1, colCount)"
          :key="'skel-col-' + col"
          class="flex-1 flex flex-col gap-3 min-w-0"
        >
          <div
            v-for="i in rowsPerPage"
            :key="'skel-' + col + '-' + i"
            class="rounded-xl overflow-hidden shimmer w-full"
            :style="skeletonCardStyle"
          />
        </div>
      </div>

      <div
        v-else-if="filteredEffects.length === 0"
        class="flex flex-col items-center justify-center py-20 text-white/40"
      >
        <UIcon name="i-lucide-image-off" class="size-12 mb-3" />
        <p class="text-sm">No effects found</p>
      </div>

      <div v-else class="flex gap-3 items-start">
        <div
          v-for="(column, colIndex) in masonryColumns"
          :key="'masonry-col-' + colIndex"
          class="flex-1 flex flex-col gap-3 min-w-0"
        >
          <!-- 外层 div：避免 <a> 内嵌 <button>（无效 HTML 会导致点击穿透到链接） -->
          <div
            v-for="effect in column"
            :key="effect.id"
            class="effect-card relative group/card rounded-xl overflow-hidden bg-white/5 w-full"
            :style="effectCardAspectStyle(effect)"
          >
          <component
            :is="props.getEffectHref ? 'a' : 'div'"
            :href="
              props.getEffectHref ? props.getEffectHref(effect) : undefined
            "
            class="relative block w-full h-full cursor-pointer no-underline text-inherit"
            @click="
              props.getEffectHref
                ? onCardClick($event, effect)
                : selectEffect(effect)
            "
          >
            <!-- 视口内才加载封面/动图，离开视口卸载以停止 WebP 解码 -->
            <EffectCardCover
              :effect="effect"
              :effect-type="activeEffectType"
              :scroll-root="useEmbeddedScroll ? gridContainerRef : null"
              :reserve-debug-space="props.isDev"
              :alt="effect.title"
            />

            <!-- 底部区域 -->
            <div
              class="absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-3 pb-3 pt-6"
            >
              <!-- 常驻标题 -->
              <p
                class="text-white text-sm font-semibold leading-tight line-clamp-2 text-center transition-opacity duration-200 group-hover/card:opacity-0 group-hover/card:pointer-events-none px-3 py-2"
              >
                {{ effect.title }}
              </p>

              <!-- 悬浮按钮 -->
              <button
                type="button"
                class="absolute inset-x-3 bottom-3 flex items-center justify-center gap-1.5 w-[calc(100%-1.5rem)] bg-[#8A53FC] hover:bg-[#7440e0] text-white text-xs font-semibold px-3 py-2 rounded-lg transition-opacity duration-200 opacity-0 group-hover/card:opacity-100"
                @click.stop="onCardClick($event, effect)"
              >
                <UIcon name="i-lucide-star" class="size-3.5" />
                Use this template
              </button>
            </div>

            <!-- Hot 标签 -->
            <div
              v-if="effect.is_hot"
              class="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full z-10"
            >
              HOT
            </div>
          </component>

          <!-- 与 <a> 平级，避免嵌套在链接内 -->
          <button
            v-if="props.isDev"
            type="button"
            class="absolute top-2 right-2 z-30 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide bg-amber-500/95 hover:bg-amber-400 text-gray-900 shadow-sm border border-amber-300/80 pointer-events-auto"
            title="在新标签页查看 workflow_config"
            @click.stop.prevent="openWorkflowDebug(effect)"
          >
            Debug
          </button>
          </div>
        </div>
      </div>

      <!-- 触底加载哨兵（IntersectionObserver，避免 scroll 高频回调） -->
      <div
        v-if="!pending && hasMore"
        ref="loadMoreSentinelRef"
        class="h-px w-full shrink-0"
        aria-hidden="true"
      />

      <!-- 加载更多 loading -->
      <div v-if="!pending && isLoadingMore" class="flex justify-center py-6">
        <UIcon
          name="i-lucide-loader-circle"
          class="size-6 text-[#8A53FC] animate-spin"
        />
      </div>

      <!-- 全部加载完 -->
      <div
        v-if="!pending && !hasMore && filteredEffects.length > 0"
        class="flex justify-center py-6 text-white/30 text-xs"
      >
        — No more effects —
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import EffectCardCover from "@/components/ai-effects/EffectCardCover.vue";
import { normalizeEffectsList } from "@/composables/useAiR2ModelsAndEffects";

// ─── Props & Emits ─────────────────────────────────────────────────────────────
const props = defineProps({
  /** 初始选中的 effect 类型，默认 'image' */
  initialEffectType: {
    type: String,
    default: "video",
  },
  /**
   * 可选：传入一个函数，接收 effect 对象，返回对应页面的 href 字符串。
   * 传入后卡片会渲染为 <a> 标签；不传则保持 <div> 行为（弹窗场景使用）。
   */
  getEffectHref: {
    type: Function,
    default: null,
  },
  /** 开发环境：显示工作流 Debug 入口 */
  isDev: {
    type: Boolean,
    default: false,
  },
});

/** 使用 localStorage：新标签页无法读取其他标签的 sessionStorage */
const EFFECT_WORKFLOW_DEBUG_STORAGE_KEY = "sharkfoto:effect-workflow-debug";

const emit = defineEmits(["select", "type-change"]);

// ─── 响应式列数（宽度判断在挂载后执行） ──────────────────────────────────────
const gridContainerRef = ref(null);
const sceneKey = computed(() => (!props.getEffectHref ? "modal" : "page"));
/** 弹窗内嵌滚动；列表页使用 layout 的页面滚动 */
const useEmbeddedScroll = computed(() => sceneKey.value === "modal");

// 维度一：场景（modal/page）
// 维度二：类型（video/image）
const DESKTOP_COL_COUNT_CONFIG = {
  modal: { video: 3, image: 3 },
  page: { video: 5, image: 4 },
};
const ROWS_PER_PAGE_CONFIG = {
  modal: { video: 3, image: 4 },
  page: { video: 3, image: 4 },
};

const getDesktopColCount = (effectType) => {
  const type = normalizeEffectType(effectType);
  return DESKTOP_COL_COUNT_CONFIG[sceneKey.value][type];
};
const colCount = ref(getDesktopColCount(props.initialEffectType));
const rowsPerPage = computed(
  () =>
    ROWS_PER_PAGE_CONFIG[sceneKey.value][
      normalizeEffectType(activeEffectType.value)
    ]
);
const pageSize = computed(() => colCount.value * rowsPerPage.value);

/** 无 cover_aspect_ratio 时与旧版 aspect-3/4 一致（宽/高 = 3/4） */
const DEFAULT_COVER_ASPECT_RATIO = 3 / 4;

function getCoverAspectRatio(effect) {
  const n = Number(effect?.cover_aspect_ratio);
  if (Number.isFinite(n) && n > 0) return n;
  return DEFAULT_COVER_ASPECT_RATIO;
}

const effectCardAspectStyle = (effect) => ({
  aspectRatio: String(getCoverAspectRatio(effect)),
});

const skeletonCardStyle = { aspectRatio: String(DEFAULT_COVER_ASPECT_RATIO) };

const updateColCount = () => {
  const desktopColCount = getDesktopColCount(activeEffectType.value);
  if (gridContainerRef.value) {
    const w = gridContainerRef.value.clientWidth;
    colCount.value = w < 480 ? 2 : w < 800 ? 3 : desktopColCount;
  } else {
    colCount.value = window.innerWidth < 640 ? 2 : desktopColCount;
  }
};

const scrollListToTop = () => {
  if (!import.meta.client) return;
  if (useEmbeddedScroll.value && gridContainerRef.value) {
    gridContainerRef.value.scrollTop = 0;
    return;
  }
  const pageScrollEl = document.querySelector(".right-scroll");
  if (pageScrollEl) {
    pageScrollEl.scrollTo({ top: 0, behavior: "auto" });
  } else {
    window.scrollTo({ top: 0, behavior: "auto" });
  }
};

const r2CachedFetch = useR2CachedFetch();
const effectIndexCache = useState("ai-effects-index-cache", () => ({}));

function normalizeEffectsIndexList(data) {
  return normalizeEffectsList(data);
}

/** 路由或父组件可能传入空字符串（如 slug 未解析到类型），否则默认 video */
function normalizeEffectType(t) {
  return t === "image" || t === "video" ? t : "video";
}

// ─── Tab 切换（须在数据请求逻辑之前定义 activeEffectType）────────────────────
const activeEffectType = ref(normalizeEffectType(props.initialEffectType));
const effectTypeTabs = [
  { label: "Video", value: "video" },
  { label: "Image", value: "image" },
];

const normalizeCategoryList = (effect) => {
  const rawList = Array.isArray(effect?.template_categories)
    ? effect.template_categories
    : String(effect?.template_category || "").split(",");
  const normalized = [];
  const seen = new Set();

  rawList.forEach((item) => {
    const value = String(item || "").trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    normalized.push(value);
  });

  return normalized;
};

const normalizeCategoryNameMap = (effect) => {
  const ids = normalizeCategoryList(effect);
  const rawNames = Array.isArray(effect?.category_names)
    ? effect.category_names
    : String(effect?.category_name || "").split(",");
  const map = new Map();

  ids.forEach((id, index) => {
    const label = String(rawNames[index] || "").trim() || id;
    if (!map.has(id)) map.set(id, label);
  });

  return map;
};

const selectEffectType = (type) => {
  const next = normalizeEffectType(type);
  activeEffectType.value = next;
  activeCategory.value = "all";
  searchQuery.value = "";
  emit("type-change", next);
};

// ─── 数据获取：每组件实例独立一份（勿用 useAsyncData 固定 key，否则弹窗与列表页会共享全局 payload）────────
const effectsData = ref(null);
const pending = ref(true);
let fetchRequestId = 0;

async function fetchEffects() {
  const type = normalizeEffectType(activeEffectType.value);
  const requestId = ++fetchRequestId;

  if (Array.isArray(effectIndexCache.value[type])) {
    if (requestId !== fetchRequestId) return;
    effectsData.value = effectIndexCache.value[type];
    pending.value = false;
    return;
  }

  pending.value = true;
  try {
    const data = await r2CachedFetch(`/api/r2/ai/effects/${type}/index.json`);
    if (requestId !== fetchRequestId) return;
    effectsData.value = normalizeEffectsIndexList(data);
    effectIndexCache.value = {
      ...effectIndexCache.value,
      [type]: effectsData.value,
    };
  } catch {
    if (requestId !== fetchRequestId) return;
    effectsData.value = [];
  } finally {
    if (requestId === fetchRequestId) pending.value = false;
  }
}

if (import.meta.client) {
  fetchEffects();
}
watch(activeEffectType, () => {
  if (import.meta.client) fetchEffects();
});
watch(activeEffectType, () => {
  if (!import.meta.client) return;
  updateColCount();
});

// 当 prop 变化时（弹窗重新打开同一个组件实例），同步 tab
watch(
  () => props.initialEffectType,
  (val) => {
    const next = normalizeEffectType(val);
    if (next !== activeEffectType.value) {
      activeEffectType.value = next;
      activeCategory.value = "all";
      searchQuery.value = "";
    }
  }
);

// ─── 数据过滤 ─────────────────────────────────────────────────────────────────
const currentTypeEffects = computed(() => {
  if (!Array.isArray(effectsData.value)) return [];
  const type = normalizeEffectType(activeEffectType.value);
  return effectsData.value
    .filter((e) => e.effect_type === type && e.enabled)
    .sort((a, b) => (b.sort ?? 0) - (a.sort ?? 0));
});

const categories = computed(() => {
  const map = new Map();
  currentTypeEffects.value.forEach((e) => {
    normalizeCategoryNameMap(e).forEach((label, value) => {
      if (!map.has(value)) map.set(value, label);
    });
  });
  return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
});

const activeCategory = ref("all");

// ─── 搜索 ─────────────────────────────────────────────────────────────────────
const searchQuery = ref("");

const filteredEffects = computed(() => {
  let list =
    activeCategory.value === "all"
      ? currentTypeEffects.value
      : currentTypeEffects.value.filter((e) =>
          normalizeCategoryList(e).includes(activeCategory.value)
        );

  const q = searchQuery.value.trim().toLowerCase();
  if (q) {
    list = list.filter((e) => e.title?.toLowerCase().includes(q));
  }
  return list;
});

// ─── 分页 / 无限加载 ───────────────────────────────────────────────────────────
const visibleCount = ref(0);
const isLoadingMore = ref(false);

const hasMore = computed(
  () => visibleCount.value < filteredEffects.value.length
);

const visibleEffects = computed(() =>
  filteredEffects.value.slice(0, visibleCount.value)
);

/** 最短列优先分配，使各列视觉高度尽量均衡 */
const masonryColumns = computed(() => {
  const n = Math.max(1, colCount.value);
  const columns = Array.from({ length: n }, () => []);
  const columnHeights = Array(n).fill(0);

  for (const effect of visibleEffects.value) {
    let targetCol = 0;
    for (let i = 1; i < n; i++) {
      if (columnHeights[i] < columnHeights[targetCol]) targetCol = i;
    }
    columns[targetCol].push(effect);
    columnHeights[targetCol] += 1 / getCoverAspectRatio(effect);
  }
  return columns;
});

const resetPaging = async () => {
  isLoadingMore.value = false;
  visibleCount.value = Math.min(pageSize.value, filteredEffects.value.length);

  await nextTick();
  scrollListToTop();
  await nextTick();
  bindLoadMoreObserver();
};

const loadMore = () => {
  if (!hasMore.value || isLoadingMore.value) return;
  isLoadingMore.value = true;

  visibleCount.value = Math.min(
    visibleCount.value + pageSize.value,
    filteredEffects.value.length
  );

  nextTick(() => {
    isLoadingMore.value = false;
  });
};

// 触底加载：IntersectionObserver 替代 scroll 事件
const loadMoreSentinelRef = ref(null);
let loadMoreObserver = null;

const bindLoadMoreObserver = () => {
  loadMoreObserver?.disconnect();
  loadMoreObserver = null;

  if (!import.meta.client || !loadMoreSentinelRef.value) {
    return;
  }

  const scrollRoot = useEmbeddedScroll.value ? gridContainerRef.value : null;

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadMore();
      }
    },
    {
      root: scrollRoot,
      rootMargin: "300px 0px",
      threshold: 0,
    }
  );
  loadMoreObserver.observe(loadMoreSentinelRef.value);
};

// 分类 / 搜索词 / 列表数据 变化时重置分页。
// 注意：不能仅在 activeEffectType 变化时重置——此时新类型的 JSON 可能尚未返回，
// currentTypeEffects 会暂时为空，会把 visibleCount 置 0 且新数据到达后不会再触发 watch。
watch(filteredEffects, () => {
  resetPaging();
});

watch(pageSize, () => {
  resetPaging();
});

watch(hasMore, async (more) => {
  if (!more) {
    loadMoreObserver?.disconnect();
    loadMoreObserver = null;
    return;
  }
  await nextTick();
  bindLoadMoreObserver();
});

// ─── 选中 effect ─────────────────────────────────────────────────────────────
const selectEffect = (effect) => {
  emit("select", effect);
};

/**
 * 卡片点击处理：
 * - ctrl/cmd/shift + 点击 或 中键点击 → 让浏览器自然处理（在新标签打开链接）
 * - 普通左键点击 → 阻止 <a> 的默认跳转，交由 selectEffect emit 处理 SPA 导航
 */
const onCardClick = (e, effect) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
  e.preventDefault();
  selectEffect(effect);
};

function parseWorkflowConfig(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

async function openWorkflowDebug(effect) {
  if (!import.meta.client) return;
  let workflow_config = parseWorkflowConfig(effect?.workflow_config);

  if (!workflow_config && effect?.effect_type && effect?.code) {
    try {
      const detail = await r2CachedFetch(
        `/api/r2/ai/effects/${effect.effect_type}/configs/${effect.code}.json`
      );
      workflow_config = parseWorkflowConfig(detail?.workflow_config);
    } catch {
      workflow_config = null;
    }
  }

  if (!workflow_config) return;

  try {
    localStorage.setItem(
      EFFECT_WORKFLOW_DEBUG_STORAGE_KEY,
      JSON.stringify({
        title: effect?.title ?? "",
        code: effect?.code ?? "",
        workflow_config,
      })
    );
  } catch {
    return;
  }

  const url = "/ai/effects/workflow-debug";
  window.open(url, "_blank", "noopener,noreferrer");
}

// ─── ResizeObserver 监听容器宽度变化 ─────────────────────────────────────────
let resizeObserver = null;
let resizeRaf = null;

onMounted(async () => {
  updateColCount();

  if (gridContainerRef.value && window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        updateColCount();
      });
    });
    resizeObserver.observe(gridContainerRef.value);
  }

  await resetPaging();
  await nextTick();
  bindLoadMoreObserver();
});

onBeforeUnmount(() => {
  if (resizeRaf) {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = null;
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  loadMoreObserver?.disconnect();
  loadMoreObserver = null;
});
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Shimmer 骨架屏 */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
.shimmer {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.09) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.6s infinite linear;
}

/* 跳过视口外卡片的布局/绘制，显著减轻长列表滚动压力 */
.effect-card {
  content-visibility: auto;
  contain-intrinsic-size: auto 320px;
  contain: layout style paint;
}

.effect-grid-scroll {
  overflow-anchor: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
